/**
 * Shared React-Query client instance for the diary subsystem.
 *
 * Exported as a module-level singleton (rather than pulled from
 * context) so non-hook helpers — `invalidateDiaryCache()`,
 * `forceClearAllCache()`, and the preloader — can trigger
 * invalidations and prefetches without needing a React tree.
 *
 * The `<TanstackQueryClientProvider>` in `AppProvider.tsx` wraps
 * the app with THIS SAME instance so `useQuery` hooks and these
 * imperative callers share ONE cache. Do NOT `new QueryClient()`
 * elsewhere — a second instance would double-fetch and split the
 * invalidation surface.
 */
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30s staleTime matches the pre-migration TTL on the
      // localStorage-backed cache — feels the same to users.
      staleTime: 30 * 1000,
      // 5 min gcTime means unmounted queries linger in cache for
      // 5 minutes so quick date-picker toggles stay instant.
      gcTime: 5 * 60 * 1000,
      // Diary is admin-facing; refetch-on-focus is annoying and
      // the old hook didn't do it — keep it off.
      refetchOnWindowFocus: false,
      // Retry once on transient network blips (Safari's "network
      // connection was lost") — the old hook silently ignored
      // these too.
      retry: 1,
    },
  },
});
